import React, { useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";

import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";

import moment from "moment";
import { getAllActivityLogs } from "./services/logs.services";
import ExpandableText from "../../components/common/ExpandableText";
import { clearLogsFilterDetails, setLogsFilterDetails } from "./logsSlice";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ShadowCard from "../../components/common/ShadowCard";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormDatePicker from "../../components/formFields/FormDateField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { Button } from "@progress/kendo-react-buttons";
import { LOGS_FORMS } from "../../_contstants/common";
import { Typography } from "@progress/kendo-react-common";

const Logs: React.FC = () => {
  const dispatch = useAppDispatch();

  const LogsList = useAppSelector((state) => state.log.LogsList);
  const loading = useAppSelector((state) => state.log.loading);
  const LogsFilterDetail = useAppSelector(
    (state) => state.log.LogsFilterDetail
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [formKey, setFormKey] = React.useState(1);
  const [logsFilter, setLogsFilter] = React.useState<any>({});

  useEffect(() => {
    const payload = {
      ...LogsFilterDetail,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllActivityLogs(payload));
  }, [LogsFilterDetail, pageLimit, currentPage]);

  React.useEffect(() => {
    setFormKey(formKey + 1);
    const BlockInventoryFilterDetail = {
      ...LogsFilterDetail,
      FromDate: LogsFilterDetail.FromDate
        ? moment(LogsFilterDetail.FromDate, "YYYY-MM-DD").toDate()
        : "",
      ToDate: LogsFilterDetail.ToDate
        ? moment(LogsFilterDetail.ToDate, "YYYY-MM-DD").toDate()
        : "",
    };
    setLogsFilter(BlockInventoryFilterDetail);
  }, [LogsFilterDetail]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleSubmit = async (value: any) => {
    const insertPayload = {
      FromDate: value?.FromDate
        ? moment(value?.FromDate).format("YYYY-MM-DD")
        : null,
      ToDate: value?.ToDate ? moment(value?.ToDate).format("YYYY-MM-DD") : null,
      Key: value?.Key || "",
    };
    dispatch(setLogsFilterDetails(insertPayload));
  };

  const handleClear = () => {
    dispatch(clearLogsFilterDetails());
    setFormKey(formKey + 1);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 14 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const gridRef = useRef<any>(null);

  return (
    <GridLayout cols={[{ width: "100%" }]}>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <GridLayoutItem>
        <Typography.h4 style={{ marginBottom: 10, marginLeft: "3px" }}>
          Transction Logs
        </Typography.h4>
      </GridLayoutItem>
      <GridLayoutItem>
        <Form
          onSubmit={handleSubmit}
          initialValues={logsFilter}
          key={formKey}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard
                style={{ padding: 20, paddingTop: 0, marginBottom: 20 }}
              >
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name="FromDate"
                      label="Start Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                      maxDate={moment(
                        formRenderProps.valueGetter(`ToDate`)
                      ).toDate()}
                      validator={
                        formRenderProps.valueGetter("ToDate")
                          ? requiredValidator
                          : skipValidator
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="ToDate"
                      label="End Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                      minDate={moment(
                        formRenderProps.valueGetter(`FromDate`)
                      ).toDate()}
                      validator={
                        formRenderProps.valueGetter("FromDate")
                          ? requiredValidator
                          : skipValidator
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Key"
                      label="Form Name"
                      placeholder="Select Form Name"
                      component={FormSelectionField}
                      options={LOGS_FORMS?.map((form) => ({
                        value: form.value,
                        label: form.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={1}
                    style={{
                      textAlign: "end",
                      marginTop: "36px",
                      marginRight: 10,
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label="Search"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </GridLayoutItem>
      <GridLayoutItem>
        <KendoGrid
          scrollable="scrollable"
          style={{ maxHeight: "calc(100vh - 250px)" }}
          className="responsive-table"
          filterable={false}
          data={LogsList}
          total={LogsList[0]?.TotalRecord || LogsList?.length}
          skip={currentPage * pageLimit}
          take={pageLimit}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 50, 100, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
          reorderable={true}
        >
          <Column field="ID" title="No" width={80} />
          <Column field="RecordNo" title="Reference No" width={130} />
          <Column
            field="UpdatedOn"
            title="Date & Time"
            width={150}
            cells={{
              data: (props) => (
                <td>
                  <div>
                    {moment(
                      props.dataItem.UpdatedOn,
                      "DD-MM-YYYY HH:mm"
                    ).format("DD/MM/YYYY hh:mm A")}
                  </div>
                </td>
              ),
            }}
          />
          <Column field="Key" title="Form Name" width={130} />
          <Column
            field="Description"
            title="Description"
            width={300}
            cells={{
              data: (props) =>
                props.dataItem.Description ? (
                  <ExpandableText text={props.dataItem.Description} />
                ) : (
                  <td></td>
                ),
            }}
          />
        </KendoGrid>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default Logs;
